import { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { HelpCircle, X, BookOpen } from 'lucide-react';
import { ColorModeContext } from '../../App';
import { useTranslation } from 'react-i18next';

interface HelpSectionProps {
  onClose: () => void;
}

/**
 * "Help" from the top bar: what each main area of ClinVea is for. The interactive walkthrough tour that used to start
 * from here (and from the floating tour button) has been removed; per-page help stays on each screen's "Learn more".
 */
export function HelpModal({ onClose }: HelpSectionProps) {
  const { t } = useTranslation('ui');
  const theme = useTheme();
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const helpTopics = [
    { id: 'dashboard-overview', title: t('hmGuideDashboardTitle'), description: t('hmGuideDashboardDesc') },
    { id: 'appointments-management', title: t('hmGuideAppointmentsTitle'), description: t('hmGuideAppointmentsDesc') },
    { id: 'patients-management', title: t('hmGuidePatientsTitle'), description: t('hmGuidePatientsDesc') },
    { id: 'staff-management', title: t('hmGuideStaffTitle'), description: t('hmGuideStaffDesc') },
    { id: 'billing-operations', title: t('hmGuideBillingTitle'), description: t('hmGuideBillingDesc') },
    { id: 'reports-analytics', title: t('hmGuideReportsTitle'), description: t('hmGuideReportsDesc') },
    { id: 'clinic-setup', title: t('hmGuideSetupTitle'), description: t('hmGuideSetupDesc') },
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpCircle size={24} />
            <Typography variant="h6" fontWeight={700}>
              {t('helpAndTutorials')}
            </Typography>
          </Box>
          <Button onClick={onClose} size="small" sx={{ minWidth: 'auto', p: 0.5 }} aria-label={t('close')}>
            <X size={18} />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <List sx={{ width: '100%' }}>
          {helpTopics.map((topic) => (
            <ListItem
              key={topic.id}
              sx={{
                borderRadius: 1.5,
                mb: 1,
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#e2e8f0',
                alignItems: 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                <BookOpen size={20} color={theme.palette.primary.main} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body1" fontWeight={600}>{topic.title}</Typography>}
                secondary={topic.description}
                secondaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
